import { StepTipType } from 'data/types';

type ProjectStepTipsArgs = {
  textKeys: Record<string, string>;
  hasAttomData: boolean;
  showExistingEquipment: boolean;
};

export function getProjectStepTips({
  textKeys,
  hasAttomData,
  showExistingEquipment
}: ProjectStepTipsArgs): Record<string, StepTipType[] | undefined> {
  const getStepText = (
    stepId: string,
    field: `tip_${number}_title` | `tip_${number}_description`,
    fallback: string
  ) => {
    return textKeys[`step_${stepId}_${field}`] ?? fallback;
  };

  return {
    location: [
      {
        targets: ['project-address-info'],
        title: getStepText('location', 'tip_1_title', 'Start with the address'),
        description: getStepText(
          'location',
          'tip_1_description',
          "Type the address and pick a match \u2014 we'll pull the property's size, age, and other details to prefill your project."
        )
      },
      ...(hasAttomData
        ? []
        : [
            {
              targets: ['project-zip-info'],
              title: getStepText(
                'location',
                'tip_2_title',
                'No exact address?'
              ),
              description: getStepText(
                'location',
                'tip_2_description',
                "Enter the zip code and year built instead \u2014 that's enough for climate data and sizing assumptions. Click Save when you're done."
              )
            }
          ])
    ],
    primary_or_detached: [
      {
        targets: ['project-primary-detached'],
        title: getStepText(
          'primary_or_detached',
          'tip_1_title',
          'Main home or separate building?'
        ),
        description: getStepText(
          'primary_or_detached',
          'tip_1_description',
          'Choose Primary Structure for a whole home, or Detached Structure for a garage, guest house, or ADU \u2014 each gets different sizing assumptions.'
        )
      },
      {
        targets: ['project-structure-type'],
        title: getStepText(
          'primary_or_detached',
          'tip_2_title',
          'Confirm the structure type'
        ),
        description: getStepText(
          'primary_or_detached',
          'tip_2_description',
          "We prefill these from property data when we can \u2014 adjust them if they don't match what you're working on."
        )
      }
    ],
    project_type: [
      {
        targets: ['project-system-preference'],
        title: getStepText(
          'project_type',
          'tip_1_title',
          'Pick your system type'
        ),
        description: getStepText(
          'project_type',
          'tip_1_description',
          'Choose the kind of system you want. Ductless mini splits are flexible and efficient \u2014 a great fit for most projects.'
        )
      },
      ...(showExistingEquipment
        ? [
            {
              targets: ['project-existing-equipment'],
              title: getStepText(
                'project_type',
                'tip_2_title',
                'Replacing old equipment?'
              ),
              description: getStepText(
                'project_type',
                'tip_2_description',
                "Tell us whether we're replacing an existing system \u2014 if yes, add each piece of equipment so we can plan the replacement properly."
              )
            }
          ]
        : [])
    ],
    has_existing_equipment: [
      {
        targets: ['project-has-existing-equipment'],
        title: getStepText(
          'has_existing_equipment',
          'tip_1_title',
          'Existing equipment?'
        ),
        description: getStepText(
          'has_existing_equipment',
          'tip_1_description',
          "If you have HVAC equipment today, we'll use it to recommend compatible replacements. If not, we'll skip that step."
        )
      }
    ]
  };
}
